// src/utilities/tracking.js
import { _config } from '../Constants';
import * as Sentry from "@sentry/react";
import {
  getFirestoreDocId,
  getCurrentUserId,
  getCurrentUserEmail,
  getCurrentOrganizationId,
  getCurrentOrganizationName
} from './firebase';
import { getUrlParameter } from './urlUtils';
import posthog from 'posthog-js';

// For analytics, fall back to capturedEmail when Firebase user has no email (anonymous auth)
const getEmailForAnalytics = () =>
  getCurrentUserEmail() || localStorage.getItem('capturedEmail') || null;

// Initialize Sentry
if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: _config.sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    environment: process.env.REACT_APP_DEPLOY_CONTEXT || process.env.NODE_ENV,
    release: process.env.COMMIT_REF,
    // Drop noise from Netlify's injected RUM script: its analytics beacons to
    // ingesteer.services-prod.nsvcs.net fail (ad-blockers / flaky networks) and
    // surface as "TypeError: Failed to fetch" from /.netlify/scripts/rum — a
    // third-party script, nothing actionable in our app (Sentry FRANCOFLEX-D8).
    denyUrls: [/\/\.netlify\/scripts\/rum/],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/animated-druid-dbee5a\.netlify\.app\//, /^https:\/\/app\.conversaflex\.com\//],
    // Session Replay
    replaysSessionSampleRate: 0.5, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

//
// HELPER FUNCTIONS
//

// Get partner properties for all tracking events (partner integration)
const getPartnerProperties = () => {
  const partner = getUrlParameter('partner');
  const partnerUserId = getUrlParameter('uid');
  return {
    ...(partner && { partner }),
    ...(partnerUserId && { partnerUserId }),
  };
};

const getOrganizationProperties = () => {
  const organizationId = getCurrentOrganizationId() || getUrlParameter('organizationId');
  const organizationName = getCurrentOrganizationName();

  return {
    ...(organizationName
      ? { organization_name: organizationName }
      : organizationId && { organization_id: organizationId }),
  };
};

// Function to track an event with common properties
export const trackEvent = (eventName, eventProperties = {}) => {
  const conversationId = eventProperties.conversationId || getFirestoreDocId() || 'unknown';
  const properties = {
    ...commonProperties,
    ...getPartnerProperties(), // Include partner in ALL events
    ...eventProperties,
    conversationId, // Preserve explicit IDs from post-conversation flows
  };
  posthog.capture(eventName, properties);

  console.log("🔍 Tracking " + eventName);
  console.log(properties)
};

// Function to set user properties
export const setUserProperties = (userProperties) => {
  const partnerProps = getPartnerProperties();
  const organizationProps = getOrganizationProperties();

  // Include partner as user properties for segmentation
  posthog.identify(getCurrentUserId(), {
    email: getEmailForAnalytics(),
    ...partnerProps,
    ...organizationProps,
    ...userProperties,
  });
};

//
// INIT
//

// PostHog is initialized by the PostHogProvider in src/index.js (_config.posthogKey).

// Common properties for all events
const commonProperties = {
  // appVersion: '1.0.0',
  // prompt
  // Add other common properties here
};


//
//  EVENT TRACKING
//

export const setupUserProperties = () => {
  const firebaseEmail = getCurrentUserEmail();
  const capturedEmail = localStorage.getItem('capturedEmail');
  const userEmail = firebaseEmail || capturedEmail || null;
  const isTrialUser = !firebaseEmail && !!capturedEmail;

  setUserProperties({ ...(isTrialUser && { userType: 'trial' }) });

  // Set the user ID if email is present
  if (userEmail) {
    console.log("Identified user " + userEmail);

    // Set Sentry user context
    Sentry.setUser({ email: userEmail });
  }

  // Identify user in PostHog if not already identified
  const posthogDistinctId = localStorage.getItem('posthog-distinct-id');
  if (posthog && !posthogDistinctId) {
    posthog.identify(getCurrentUserId(), {
      email: userEmail,
      ...(isTrialUser && { userType: 'trial' }),
    });
  }
}

// Example specific event trackers
export const trackModalAcknowledged = (age, language, level, showTutorial) => {
  setUserProperties({ age, language, level, showTutorial });

  trackEvent('madameai_modal_acknowledged', { age, language, level, showTutorial });
};

export const trackConversationStarted = (isRealtime, selectedActivity, consentToFollowUp) => {
  // Fetch user email directly from Firebase
  const { getCurrentUserEmail } = require('./firebase');
  const userEmail = getCurrentUserEmail();
  const capturedEmail = localStorage.getItem('capturedEmail');
  const workIntent = localStorage.getItem('workIntent');

  const eventProperties = {
    isRealtime,
    selectedActivity,
    trial: localStorage.getItem('trialSession') === 'true',
  };
  if (consentToFollowUp !== null) {
    eventProperties.consentToFollowUp = consentToFollowUp;
  }
  if (userEmail) {
    eventProperties.userEmail = userEmail;
  }
  if (capturedEmail) {
    eventProperties.capturedEmail = capturedEmail;
  }
  if (workIntent) {
    eventProperties.workIntent = workIntent;
  }
  trackEvent('madameai_conversation_started', eventProperties); // User clicks on "commencer"
};

export const trackTutorialEnded = () => {
  trackEvent('madameai_tutorial_ended'); // User clicks on "commencer"
};

export const trackConversationSendMessage = (content, role) => {
  trackEvent('madameai_sent_message', { content, role }); // User clicks on "commencer"
};

/**
 * A clip was recorded but never produced an assistant reply. `reason` names the guard
 * or failure that released the microphone, so a spike in any single cause is visible
 * without waiting for a support ticket (MAD-788).
 */
export const trackRecordingTurnFailed = (activityId, reason, details = {}) => {
  trackEvent('madameai_recording_turn_failed', { activityId, reason, ...details });
};

/**
 * The streamed /turn/stream path failed before any audio played and the turn was
 * re-run through the legacy /upload path. The reason (http_401, network_error,
 * unsupported_protocol, missing_id_token, ...) is what tells us whether the streamed
 * rollout is healthy or silently degrading to legacy for everyone.
 */
export const trackTurnStreamFallback = (activityId, reason, details = {}) => {
  trackEvent('madameai_turn_stream_fallback', { activityId, reason, ...details });
};

/**
 * The stuck-recording watchdog fired: the microphone stayed locked with no reply for
 * longer than a turn can legitimately take, and was force-released so the learner
 * could continue instead of reloading the page.
 */
export const trackRecordingStuckRecovered = (activityId, stuckForMs) => {
  trackEvent('madameai_recording_stuck_recovered', { activityId, stuckForMs });
};

export const trackRealtimeStartStuckRecovered = (activityId, stuckForMs) => {
  trackEvent('madameai_realtime_start_stuck_recovered', { activityId, stuckForMs });
};

export const trackMicrophoneDeadRecovered = (activityId, audioContextState) => {
  trackEvent('madameai_microphone_dead_recovered', { activityId, audioContextState });
};

export const trackConversationEnded = (url, activityId, feedback) => {
  const isRealtime = localStorage.getItem('isRealtime') === 'true'
  const resolvedActivityId = activityId || localStorage.getItem('selectedActivity') || null;
  trackEvent('madameai_conversation_ended', {
    url,
    isRealtime,
    activityId: resolvedActivityId,
    ...(resolvedActivityId && { selectedActivity: resolvedActivityId }),
    feedbackRating: feedback?.rating || null,
    feedbackTags: feedback?.tags || [],
    feedbackComment: feedback?.comment || null
  })
};

export const trackFeedbackSubmitted = (conversationId, activityId, feedback) => {
  const resolvedActivityId = activityId || localStorage.getItem('selectedActivity') || null;
  trackEvent('madameai_feedback_submitted', {
    conversationId,
    activityId: resolvedActivityId,
    ...(resolvedActivityId && { selectedActivity: resolvedActivityId }),
    feedbackRating: feedback?.rating || null,
    feedbackTags: feedback?.tags || [],
    feedbackComment: feedback?.comment || null
  })
};

export const trackFeedbackSurveySent = (surveyId, feedbackText) => {
  trackEvent('survey_sent', { feedbackText });

  if (posthog && feedbackText.trim()) {
		const properties = {
			...commonProperties,
			$survey_id: surveyId,
			$survey_response: feedbackText,
			conversationId: getFirestoreDocId() || 'unknown', // Include common props if desired
		}
		posthog.capture('survey sent', properties)
		console.log('🔍 Tracking survey sent')
		return true // Indicate success
	} else if (!feedbackText.trim()) {
		console.warn('Feedback text is empty, not sending survey.')
		return false // Indicate failure (empty text)
	} else {
		console.error('PostHog not available, cannot send survey.')
		return false // Indicate failure (PostHog unavailable)
	}

}

export const trackSentenceFeedback = (conversationId, sentence, audioIndex, isPositive) => {
  console.log(`🎭 madameai_sentence_feedback happened - ${isPositive ? 'thumbs up' : 'thumbs down'} for audio ${audioIndex}`);
  trackEvent('madameai_sentence_feedback', {
    conversationId,
    sentence,
    audioIndex,
    isPositive
  });
};

export const trackUserSentenceFeedback = (conversationId, sentence, messageIndex, isPositive, audioUrl) => {
  console.log(`🎭 madameai_user_sentence_feedback happened - ${isPositive ? 'thumbs up' : 'thumbs down'} for message ${messageIndex} with audio ${audioUrl}`);
  trackEvent('madameai_user_sentence_feedback', {
    conversationId,
    sentence,
    messageIndex,
    isPositive,
    audioUrl
  });
};

export const trackPlannerCreated = (goal, numberOfActivities, level, age, userLanguage) => {
  console.log(`🗺️ Planner created with goal: ${goal}, ${numberOfActivities} activities`);
  trackEvent('madameai_planner_created', {
    goal: goal || 'unknown',
    numberOfActivities: numberOfActivities || 0,
    level: level || 'unknown',
    age: age || 'unknown',
    userLanguage: userLanguage || 'unknown',
    userId: getCurrentUserId() || 'unknown'
  });
};

export const trackLearningGoalCreated = (goal, level, age, userLanguage) => {
  console.log(`📝 Learning goal created: ${goal}`);
  trackEvent('madameai_learning_goal_created', {
    goal: goal || 'unknown',
    level: level || 'unknown',
    age: age || 'unknown',
    userLanguage: userLanguage || 'unknown',
    userId: getCurrentUserId() || 'unknown'
  });
};

export const trackLearningGoalEdited = (oldGoal, newGoal, level, age, userLanguage) => {
  console.log(`✏️ Learning goal edited from "${oldGoal}" to "${newGoal}"`);
  trackEvent('madameai_learning_goal_edited', {
    oldGoal: oldGoal || 'unknown',
    newGoal: newGoal || 'unknown',
    level: level || 'unknown',
    age: age || 'unknown',
    userLanguage: userLanguage || 'unknown',
    userId: getCurrentUserId() || 'unknown'
  });
};

export const trackOnboardingStep = (step, value) => {
  console.log(`🎓 Onboarding step completed: ${step} = ${value}`);
  trackEvent('madameai_onboarding_step', {
    step,
    value,
    userId: getCurrentUserId() || 'unknown'
  });
};
